import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppointmentDetails } from '../../components/appointments/DayView/components/AppointmentDetails';
import {
  ArrowRight,
  Edit2,
  Ban,
  Star,
  Diamond,
  History,
  Award,
  Lock,
  Phone,
  Calendar,
  MapPin,
  Clock,
  FileText,
  MessageSquare,
  Tag,
  User,
  Scissors,
  Package,
  CreditCard,
  AlertCircle,
  ArrowLeft,
  Repeat,
  Edit,
  Trash2,
  Plus,
  ShoppingBag,
  Trophy,
  X,
  MessageCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Database } from '../../lib/database.types';
import { CustomerForm } from './components/CustomerForm';
import { useSubscription } from '../../hooks/useSubscription';
import { useLoyaltySettings } from '../../hooks/useLoyaltySettings';
import toast from 'react-hot-toast';
import { format, parseISO, subMonths } from 'date-fns';
import { he } from 'date-fns/locale';
import { RegularAppointmentModal } from './components/RegularAppointmentModal';

interface Customer {
  id: string;
  business_id: string;
  name: string;
  phone: string;
  email: string | null;
  points: number;
  diamonds: number;
  loyalty_level: 'silver' | 'gold' | 'diamond' | 'vip';
  loyalty_stats: {
    total_visits: number;
    total_spent: number;
    last_visit: string;
    achievements: string[];
    consecutive_visits: number;
  };
  metadata: {
    address?: string;
    birth_date?: string;
    city?: string;
    tags?: string[];
    blocked?: boolean;
  } | null;
  last_login?: string;
  last_visit?: string;
  total_purchases: number;
  total_spent: number;
  average_purchase: number;
  visit_frequency: 'high' | 'medium' | 'low';
  created_at: string;
  updated_at: string;
}

interface Service {
  id: string;
  price: number;
}

interface Appointment {
  id: string;
  start_time: string;
  status: string;
  services: Service | Service[];
  users: {
    name: string;
  };
}

type TabType = 'loyalty' | 'documents' | 'purchases' | 'visits';

interface LoyaltyBenefits {
  products_discount: number;
  services_discount: number;
  birthday_appointment: boolean;
  free_appointment_every: number | null;
}

interface LoyaltyLevel {
  benefits: LoyaltyBenefits;
  diamonds_required: number;
}

interface BusinessSettings {
  loyalty: {
    levels: Record<string, LoyaltyLevel>;
    points: {
      per_visit: number;
      per_amount: number;
      per_referral: number;
      expiration_days: number;
    };
    enabled: boolean;
    diamonds: {
      per_amount: number;
      per_consecutive_visits: number;
      consecutive_visits_required: number;
    };
  };
}

const getLoyaltyLabel = (level: string) => {
  switch (level) {
    case 'vip':
      return 'VIP';
    case 'diamond':
      return 'יהלום';
    case 'gold':
      return 'זהב';
    default:
      return 'כסף';
  }
};

const formatBenefits = (benefits: LoyaltyBenefits) => {
  const parts = [];
  if (benefits.services_discount) {
    parts.push(`הנחה של ${benefits.services_discount}% על שירותים`);
  }
  if (benefits.products_discount) {
    parts.push(`הנחה של ${benefits.products_discount}% על מוצרים`);
  }
  if (benefits.free_appointment_every) {
    parts.push(`תור חינם כל ${benefits.free_appointment_every} תורים`);
  }
  if (benefits.birthday_appointment) {
    parts.push('הטבת יום הולדת');
  }
  return parts.join(', ');
};

// הוסף אייקון וואטסאפ SVG אמיתי
const WhatsAppIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    viewBox="0 0 32 32"
    fill="currentColor"
    width={props.width || 24}
    height={props.height || 24}
  >
    <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.637 1.934 6.627L4 29l7.573-1.934A12.97 12.97 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22.917c-2.162 0-4.28-.634-6.09-1.83l-.434-.273-4.504 1.15 1.2-4.39-.282-.447A10.93 10.93 0 015.083 15c0-6.032 4.885-10.917 10.917-10.917S26.917 8.968 26.917 15 22.032 25.917 16 25.917zm5.49-7.13c-.301-.15-1.782-.879-2.057-.98-.275-.101-.476-.15-.676.15-.2.301-.776.98-.951 1.181-.175.2-.351.225-.651.075-.301-.15-1.271-.468-2.423-1.492-.896-.799-1.502-1.787-1.679-2.088-.175-.301-.019-.464.131-.614.134-.134.301-.351.451-.526.15-.175.2-.301.301-.501.1-.2.05-.376-.025-.526-.075-.15-.676-1.632-.926-2.236-.244-.587-.492-.507-.676-.517l-.576-.01c-.2 0-.526.075-.801.376-.275.301-1.05 1.025-1.05 2.5s1.076 2.899 1.226 3.099c.15.2 2.119 3.234 5.137 4.399.719.247 1.28.395 1.718.505.722.183 1.38.157 1.899.095.579-.069 1.782-.728 2.035-1.432.253-.704.253-1.308.177-1.432-.075-.124-.275-.2-.576-.351z"/>
  </svg>
);

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFeatureAvailable } = useSubscription();
  const { isLoyaltyEnabled } = useLoyaltySettings();
  const [customer, setCustomer] = useState<Customer | null>(null);
  // const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [showEditForm, setShowEditForm] = useState(false);
  const [completedAppointments, setCompletedAppointments] = useState(0);
  const [recentAppointments, setRecentAppointments] = useState<any[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'appointments' | 'loyalty' | 'documents'>('info');
  const [showRegularAppointmentModal, setShowRegularAppointmentModal] = useState(false);
  const [lastAppointment, setLastAppointment] = useState<any>(null);
  const [yearlyVisitCount, setYearlyVisitCount] = useState<number>(0);
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings | null>(null);
  const [businessSettingsLoading, setBusinessSettingsLoading] = useState(true);
  const [showBlockPopup, setShowBlockPopup] = useState(false);

  // בדיקה אם תכונת תכנית הנאמנות זמינה במנוי וגם מופעלת בהגדרות
  const loyaltyEnabled = isFeatureAvailable('loyalty_program') && isLoyaltyEnabled;
useEffect(() => {
  if (customer?.id) {
    loadInvoices();
  }
}, [customer?.id]);

const loadInvoices = async () => {
  const { data, error } = await supabase
    .from('payment_transactions')
    .select('id, amount, created_at, invoice_link')
    .eq('customer_id', customer.id)
    .not('invoice_link', 'is', null)
    .order('created_at', { ascending: false });

  if (!error && data) setInvoices(data);
};
  useEffect(() => {
    if (id === 'new') {
      navigate('/customers', { replace: true });
    }
  }, [id]);

  if (id === 'new') return null;
  useEffect(() => {
    loadCustomer();
    loadBusinessSettings();
  }, [id]);

  // הוספת useEffect שמרענן את businessSettings אחרי טעינת לקוח
  useEffect(() => {
    if (customer?.business_id) {
      loadBusinessSettings();
    }
  }, [customer?.business_id]);

  const calculateLoyaltyLevel = (
    diamonds: number,
    levels: Record<string, LoyaltyLevel>
  ) => {
    // סדר את הרמות לפי diamonds_required מהנמוך לגבוה
    const sorted = Object.entries(levels)
      .sort(([, a], [, b]) => a.diamonds_required - b.diamonds_required);

    // מצא את הרמה הגבוהה ביותר שהלקוח עומד בדרישתה בלבד
    let currentLevel = sorted[0][0];
    for (let i = 0; i < sorted.length; i++) {
      const [level, config] = sorted[i];
      if (diamonds < config.diamonds_required) {
        // ברגע שלא עומד בדרישה, עצור וחזור לרמה הקודמת
        break;
      }
      currentLevel = level;
    }
    return currentLevel;
  };

  const loadCustomer = async () => {
    if (!id || id === 'new') {
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setCustomer(data);

      // Load all appointments
      const { data: allAppointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select(`
id,
start_time,
end_time,
status,
business_id,
customer_id,
service_id,
staff_id,
metadata,
customers (
  name,
  phone
),
services (
  name_he
),
users (
  name
)
`)
        .eq('customer_id', id)
        .order('start_time', { ascending: false });

      // הצג את כל התורים כולל מבוטלים
      if (!appointmentsError) {
        const recent = (allAppointments || [])
          // .filter((a) => a.status !== 'canceled') // הסר את הסינון הזה!
          .slice(0, 3);

        setRecentAppointments(recent);
      }

      // Update customer with real statistics
      const completedAppointments = allAppointments?.filter((apt: Appointment) => apt.status === 'completed') || [];
      const totalSpent = completedAppointments.reduce((sum: number, app: Appointment) => {
        // Check if services is an array or a single object
        const services = Array.isArray(app.services) ? app.services : [app.services];
        const servicePrices = services.reduce((total: number, service: Service | null) => {
          if (!service) return total;
          return total + (service.price || 0);
        }, 0);
        return sum + servicePrices;
      }, 0);
      const averagePurchase = completedAppointments.length > 0 ? totalSpent / completedAppointments.length : 0;

      // Calculate visit frequency
      let visitFrequency: 'high' | 'medium' | 'low' = 'low';
      if (completedAppointments.length >= 12) {
        visitFrequency = 'high';
      } else if (completedAppointments.length >= 6) {
        visitFrequency = 'medium';
      }

      // עדכון רמת נאמנות לפי כמות היהלומים והגדרות העסק
      let loyaltyLevel = data.loyalty_level;
      if (businessSettings?.loyalty?.levels) {
        loyaltyLevel = calculateLoyaltyLevel(data.diamonds, businessSettings.loyalty.levels);
      }

      // Update customer with real statistics
      const updatedCustomer = {
        ...data,
        business_id: data.business_id,
        total_purchases: completedAppointments.length,
        total_spent: totalSpent,
        average_purchase: averagePurchase,
        visit_frequency: visitFrequency
        // loyalty_level: לא לחשב כאן!
      };
      setCustomer(updatedCustomer);
      setCompletedAppointments(completedAppointments.length);

      fetchLastAppointment(data.id);
    } catch (error: any) {

      toast.error('שגיאה בטעינת פרטי הלקוח');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessSettings = async () => {
    try {
      if (!customer?.business_id) return;
      setBusinessSettingsLoading(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('settings')
        .eq('id', customer.business_id)
        .single();
      if (error) throw error;
      setBusinessSettings(data.settings);
    } catch (error) {

    } finally {
      setBusinessSettingsLoading(false);
    }
  };

  const fetchLastAppointment = async (customerId: string) => {
    try {
      const now = new Date();

      // Get the last completed appointment
      const { data: lastAppointmentData, error: lastAppointmentError } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          services (
            name_he
          ),
          status
        `)
        .eq('customer_id', customerId)
        .eq('status', 'completed')
        .order('start_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!lastAppointmentError && lastAppointmentData) {
        setLastAppointment(lastAppointmentData);
      }

      // Calculate number of completed appointments in the last year
      const yearAgo = subMonths(now, 12);

      const { count, error: countError } = await supabase
        .from('appointments')
        .select('id', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .eq('status', 'completed')
        .gte('start_time', yearAgo.toISOString());

      if (!countError && count !== null) {
        setYearlyVisitCount(count);
      }
    } catch (error) {

    }
  };

  const handleSubmit = async (data: Partial<Customer>) => {
    if (!customer) return;

    try {
      const { error } = await supabase
        .from('customers')
        .update(data)
        .eq('id', customer.id);

      if (error) throw error;
      toast.success('הלקוח עודכן בהצלחה');
      setShowEditForm(false);
      loadCustomer();
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בעדכון הלקוח');
    }
  };

  const handleBlock = async () => {
    if (!customer) return;

    try {
      // קרא את is_blocked מהאובייקט customer (ולא מה-type)
      const isBlocked = (customer as any).is_blocked || false;

      const { error } = await supabase
        .from('customers')
        .update({
          is_blocked: !isBlocked,
          // עדכן גם metadata.blocked לשמירה על תאימות ישנה
          metadata: {
            ...customer.metadata,
            blocked: !isBlocked
          }
        })
        .eq('id', customer.id);

      if (error) throw error;

      toast.success(
        isBlocked
          ? 'הלקוח שוחרר בהצלחה'
          : 'הלקוח נחסם בהצלחה'
      );
      // פתח את הפופאפ, אל תסגור אוטומטית
      if (!isBlocked) {
        setShowBlockPopup(true);
        // הסר את setTimeout לסגירה אוטומטית
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('refresh-customers'));
      }

      // טען מחדש את הלקוח כדי לקבל את הערך החדש של is_blocked
      await loadCustomer();
    } catch (error) {

      toast.error('שגיאה בחסימת הלקוח');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'booked':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">ממתין לאישור</span>;
      case 'confirmed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">מאושר</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">הושלם</span>;
      case 'canceled':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">בוטל</span>;
      case 'no_show':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">לא הגיע</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">{status}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '0 ₪';
    return value.toLocaleString('he-IL') + ' ₪';
  };

 

  const renderLoyaltyStats = () => {
    if (!customer) return null;

    // השתמש ב-loyaltyLevel המחושב ולא ב-customer.loyalty_level
    const loyaltyLevel =
      customer && businessSettings?.loyalty?.levels
        ? calculateLoyaltyLevel(customer.diamonds, businessSettings.loyalty.levels)
        : customer?.loyalty_level;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">יהלומים</h3>
            <p className="text-2xl font-bold">{customer?.diamonds}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">רמת נאמנות</h3>
            {businessSettingsLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <p className="text-2xl font-bold">{getLoyaltyLabel(loyaltyLevel)}</p>
            )}
          </div>
        </div>

        {/* הוספת כרטיס להצגת כמות נקודות */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">נקודות</h3>
          <p className="text-2xl font-bold">{customer?.points || 0}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (id === 'new') {
    return null; // או מודאל יצירת לקוח, או ריק
  }

  if (!customer) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500">לא נמצא לקוח</p>
        <Link
          to="/customers"
          className="text-indigo-600 hover:text-indigo-700"
        >
          חזרה לרשימת הלקוחות
        </Link>
      </div>
    );
  }

  // חישוב רמת נאמנות בפועל לפי היהלומים והגדרות העסק
  const loyaltyLevel =
    customer && businessSettings?.loyalty?.levels
      ? calculateLoyaltyLevel(customer.diamonds, businessSettings.loyalty.levels)
      : null;

  // חישוב המעמד הבא לפי הרמה המחושבת בפועל
  const nextLevelProgress = () => {
    if (!customer || !businessSettings?.loyalty?.levels) return null;

    // סדר הדרגות לפי diamonds_required מהנמוך לגבוה
    const levelsKeys = Object.keys(businessSettings.loyalty.levels);
    const sortedLevels = levelsKeys
      .map(key => ({
        key,
        diamonds_required: businessSettings.loyalty.levels[key].diamonds_required
      }))
      .sort((a, b) => a.diamonds_required - b.diamonds_required)
      .map(l => l.key);

    // מצא את האינדקס של הרמה המחושבת בפועל
    const currentIndex = sortedLevels.indexOf(loyaltyLevel);
    const nextIndex = currentIndex + 1;

    if (nextIndex >= sortedLevels.length) return null;

    const nextLevelKey = sortedLevels[nextIndex];
    const nextLevel = businessSettings.loyalty.levels[nextLevelKey];
    if (!nextLevel) return null;

    const requiredDiamonds = nextLevel.diamonds_required;
    const remaining = Math.max(requiredDiamonds - customer.diamonds, 0);
    const progress = Math.min((customer.diamonds / requiredDiamonds) * 100, 100);

    return {
      next: nextLevelKey,
      nextLabel: getLoyaltyLabel(nextLevelKey),
      progress,
      remaining,
      benefits: formatBenefits(nextLevel.benefits)
    };
  };

  const getCurrentLevelBenefits = () => {
    const levels = {
      silver: 'אין הטבות מיוחדות',
      gold: 'הנחה של 5% על שירותים ומוצרים',
      diamond: 'הנחה של 10% על שירותים ומוצרים, תור חינם כל 10 תורים',
      vip: 'הנחה של 20% על שירותים ומוצרים, תור חינם כל 5 תורים, הטבת יום הולדת, שירות VIP'
    };

    return levels[customer.loyalty_level as keyof typeof levels];
  };

  const getLoyaltyColor = (level: string) => {
    switch (level) {
      case 'vip':
        return 'bg-purple-100 text-purple-800';
      case 'diamond':
        return 'bg-blue-100 text-blue-800';
      case 'gold':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4 pb-32">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          {/* כפתורי עריכה וחסימה */}
          <div className="absolute left-0 top-0 flex items-center gap-2 z-10">
            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={() => setShowEditForm(true)}
              className="p-2 bg-blue-50 rounded-full text-blue-600 hover:bg-blue-100 active:bg-blue-200 flex-shrink-0 transition-colors"
            >
              <Edit className="h-5 w-5" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={handleBlock}
              className={`p-2 rounded-full flex-shrink-0 transition-colors ${
                customer.metadata?.blocked
                  ? 'bg-red-50 text-red-500 hover:bg-red-100 active:bg-red-200'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 active:bg-gray-300'
              }`}
            >
              <Ban className="h-5 w-5" />
            </motion.button>
          </div>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Link to="/customers" className="text-gray-500 hover:text-gray-700 flex-shrink-0">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-blue-600">
                  {customer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <div
                className="min-w-0"
                style={
                  window.innerWidth < 640
                    ? {
                        maxWidth: 'calc(100vw - 120px)', // 120px for avatar + buttons + padding
                        overflow: 'hidden'
                      }
                    : {}
                }
              >
                <h1
                  className="text-xl font-semibold text-gray-900"
                  style={
                    window.innerWidth < 640
                      ? {
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          display: 'block'
                        }
                      : {}
                  }
                >
                  {customer.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <a
                    href={`tel:${customer.phone}`}
                    className="text-blue-500 hover:text-blue-600 flex items-center gap-1.5 flex-shrink-0 text-sm"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span>{customer.phone}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {(customer.metadata?.blocked || (customer as any).is_blocked) && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-500 rounded-full text-xs font-medium">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>חסום</span>
            </div>
          )}

          {loyaltyEnabled && (
            businessSettingsLoading ? (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-gray-50 text-gray-500">
                <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span>טוען...</span>
              </div>
            ) : (
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${loyaltyLevel === 'vip'
                  ? 'bg-purple-50 text-purple-600'
                  : loyaltyLevel === 'diamond'
                    ? 'bg-blue-50 text-blue-600'
                    : loyaltyLevel === 'gold'
                      ? 'bg-amber-50 text-amber-600'
                      : 'bg-gray-50 text-gray-600'
                }`}>
                <Award className="h-3.5 w-3.5" />
                <span>
                  {loyaltyLevel === 'vip'
                    ? 'VIP'
                    : loyaltyLevel === 'diamond'
                      ? 'יהלום'
                      : loyaltyLevel === 'gold'
                        ? 'זהב'
                        : 'כסף'}
                </span>
              </div>
            )
          )}

          {customer.metadata?.tags && Array.isArray(customer.metadata.tags) && customer.metadata.tags.map((tag: string, index: number) => (
            <div key={index} className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
              <Tag className="h-3.5 w-3.5" />
              <span>{tag}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex-1 py-2.5 px-4 text-center text-sm font-medium transition-colors ${activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-500'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            פרטים
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex-1 py-2.5 px-4 text-center text-sm font-medium transition-colors ${activeTab === 'appointments'
                ? 'text-blue-600 border-b-2 border-blue-500'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            תורים
          </button>
          {loyaltyEnabled && (
            <button
              onClick={() => setActiveTab('loyalty')}
              className={`flex-1 py-2.5 px-4 text-center text-sm font-medium transition-colors ${activeTab === 'loyalty'
                  ? 'text-blue-600 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              נאמנות
            </button>
          )}
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex-1 py-2.5 px-4 text-center text-sm font-medium transition-colors ${activeTab === 'documents'
                ? 'text-blue-600 border-b-2 border-blue-500'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            מסמכים
          </button>
        </div>

        {/* Content based on active tab */}
        <div className="p-4">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-900">
                  <User className="h-4 w-4 text-blue-500" aria-hidden="true" />
                  פרטים אישיים
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-xs font-medium text-gray-500">שם מלא</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{customer.name}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">טלפון</span>
                    </div>
                    <p className="text-lg font-medium">{customer.phone}</p>
                  </div>
                  <div
                    className="bg-gray-50 p-4 rounded-lg"
                    style={{
                      // במובייל: תן לכרטיסייה להתרחב עד 90vw, בדסקטופ תשאיר רגיל
                      maxWidth: window.innerWidth < 640 ? '90vw' : undefined,
                      minWidth: window.innerWidth < 640 ? '70vw' : undefined,
                      width: window.innerWidth < 640 ? 'auto' : undefined,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">אימייל</span>
                    </div>
                    <p
                      className="text-lg font-medium"
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        width: '100%',
                        margin: 0,
                      }}
                    >
                      {customer.email || '-'}
                    </p>
                  </div>
                  {customer.metadata?.birth_date && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        <span className="text-sm font-medium">תאריך לידה</span>
                      </div>
                      <p className="text-lg font-medium">
                        {new Date(customer.metadata.birth_date).toLocaleDateString('he-IL')}
                      </p>
                    </div>
                  )}
                  {customer.metadata?.city && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        <span className="text-sm font-medium">עיר</span>
                      </div>
                      <p className="text-lg font-medium">{customer.metadata.city}</p>
                    </div>
                  )}
                  {customer.metadata?.tags && customer.metadata.tags.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        <span className="text-sm font-medium">תגיות</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customer.metadata.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer History */}
              <div className="mt-8">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <History className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                  היסטוריית לקוח
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">תאריך הצטרפות</span>
                    </div>
                    <p className="text-lg font-medium">
                      {new Date(customer.created_at).toLocaleDateString('he-IL')}
                    </p>
                  </div>
                  {/* <div className="bg-gray-50 p-4 rounded-lg"> */}
                    {/* <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">ביקור אחרון</span>
                    </div> */}
                    {/* <p className="text-lg font-medium">
                      {customer.last_visit
                        ? new Date(customer.last_visit).toLocaleDateString('he-IL')
                        : 'טרם ביקר'}
                    </p> */}
                  {/* </div> */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingBag className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">סה"כ רכישות</span>
                    </div>
                    <p className="text-lg font-medium">{customer.total_purchases}</p>
                  </div>
                  {/* <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">תדירות ביקורים</span>
                    </div>
                    <p className="text-lg font-medium">
                      {customer.visit_frequency === 'high'
                        ? 'גבוהה'
                        : customer.visit_frequency === 'medium'
                          ? 'בינונית'
                          : 'נמוכה'}
                    </p>
                  </div> */}
                  {/* הסר ממוצע ביקור וסה"כ הוצאות */}
                  {/* <div className="bg-gray-50 p-4 rounded-lg"> ... </div> */}
                  {/* <div className="bg-gray-50 p-4 rounded-lg"> ... </div> */}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'appointments' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  היסטוריית תורים
                </h2>
                <div className="text-sm text-gray-500">
                  סה"כ: {completedAppointments} תורים
                </div>
              </div>

              {recentAppointments.length > 0 ? (
                <div className="space-y-4">
                  {recentAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      onClick={() => setSelectedAppointment(appointment)}
                      className="cursor-pointer bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-lg">{appointment.services.name_he}</div>
                          <div className="text-sm text-gray-500 mt-1">עם {appointment.users.name}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatDate(appointment.start_time)}</div>
                          <div className="text-sm text-gray-500">{formatTime(appointment.start_time)}</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                        {getStatusBadge(appointment.status)}
                      </div>
                    </div>
                  ))}

                  <div className="text-center mt-4">
                    <Link
                      to={`/appointments?customer=${customer.id}`}
                      className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      עבור ליומן
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">אין תורים קודמים</p>
                </div>
              )}

              {/* 👇 הצגת המודאל */}
              {selectedAppointment && (
                <AppointmentDetails
                  appointment={selectedAppointment}
                  onClose={() => setSelectedAppointment(null)}
                  onUpdate={() => {
                    loadCustomer(); // מרענן את כל הנתונים אחרי שינוי בתור
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'loyalty' && loyaltyEnabled && (
            <div className="space-y-6">
              {/* Loyalty Level */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Award className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                  רמת נאמנות
                </h2>
                {businessSettingsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                          loyaltyLevel === 'vip'
                            ? 'bg-purple-100'
                            : loyaltyLevel === 'diamond'
                              ? 'bg-blue-100'
                              : loyaltyLevel === 'gold'
                                ? 'bg-amber-100'
                                : 'bg-gray-100'
                        }`}>
                        <Award
                          className={`h-8 w-8 ${
                              loyaltyLevel === 'vip'
                                ? 'text-purple-600'
                                : loyaltyLevel === 'diamond'
                                  ? 'text-blue-600'
                                  : loyaltyLevel === 'gold'
                                    ? 'text-amber-600'
                                    : 'text-gray-600'
                            }`}
                          aria-hidden="true"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">
                          {getLoyaltyLabel(loyaltyLevel)}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {loyaltyLevel === 'vip'
                            ? 'רמת נאמנות הגבוהה ביותר'
                            : loyaltyLevel === 'diamond'
                              ? 'רמת נאמנות גבוהה'
                              : loyaltyLevel === 'gold'
                                ? 'רמת נאמנות בינונית-גבוהה'
                                : 'רמת נאמנות בסיסית'}
                        </p>
                      </div>
                    </div>

                    {businessSettings?.loyalty?.levels[loyaltyLevel] && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">הטבות נוכחיות:</h3>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                          {formatBenefits(businessSettings.loyalty.levels[loyaltyLevel].benefits)}
                        </p>
                      </div>
                    )}

                    {/* כרטיסיית המעמד הבא שלך */}
                    {nextLevelProgress() && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">המעמד הבא שלך:</h3>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Award className="h-6 w-6 text-indigo-600" />
                          </div>
                          <div>
                            {/* הצג את שם הרמה בעברית ותקטין את הגודל */}
                            <p className="font-semibold text-xs">{getLoyaltyLabel(nextLevelProgress()?.next || '')}</p>
                            <p className="text-xs text-gray-600">{nextLevelProgress()?.benefits}</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span>{customer.diamonds} יהלומים</span>
                            <span>נדרשים עוד {nextLevelProgress()?.remaining} יהלומים</span>
                          </div>
                          <div className="w-full bg-white rounded-full h-2.5">
                            <div
                              className="bg-indigo-600 h-2.5 rounded-full"
                              style={{ width: `${Math.min((nextLevelProgress()?.progress || 0), 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Loyalty Stats */}
              {renderLoyaltyStats()}

              {/* Visit History */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <History className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                  היסטוריית ביקורים
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">ביקור אחרון</span>
                    </div>
                    <p className="text-lg font-medium">
                      {customer.loyalty_stats.last_visit
                        ? new Date(customer.loyalty_stats.last_visit).toLocaleDateString('he-IL')
                        : 'טרם ביקר'}
                    </p>
                  </div>
                  {/* {customer.loyalty_stats.consecutive_visits > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        <span className="text-sm font-medium">ביקורים רצופים</span>
                      </div>
                      <p className="text-lg font-medium">{customer.loyalty_stats.consecutive_visits}</p>
                    </div> */}
                  {/* )} */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      <span className="text-sm font-medium">סה"כ ביקורים</span>
                    </div>
                    <p className="text-lg font-medium">{customer.loyalty_stats.total_visits}</p>
                  </div>
                  {customer.loyalty_stats.achievements.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        <span className="text-sm font-medium">הישגים</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customer.loyalty_stats.achievements.map((achievement) => (
                          <span
                            key={achievement}
                            className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm"
                          >
                            {achievement}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                מסמכים וחשבוניות
              </h2>

 <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
  {invoices.length === 0 ? (
    <div className="text-center py-8">
      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
      <p className="text-gray-500">אין מסמכים זמינים</p>
      <button
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        onClick={() => navigate('/invoices/new', { state: { customerId: customer.id } })}
      >
        צור חשבונית חדשה
      </button>
    </div>
  ) : (
    <div className="space-y-4">
      {invoices.map(inv => (
  <div key={inv.id} className="flex justify-between items-center border-b pb-2">
    <div>
      <div className="font-medium">
        חשבונית {inv.invoice_number ? `#${inv.invoice_number}` : ''}
      </div>
      <div className="text-sm text-gray-500">
        {new Date(inv.created_at).toLocaleDateString('he-IL')}
      </div>
      <div className="text-sm text-gray-500">
        סכום: {inv.amount} ₪
      </div>
    </div>
    <a
      href={inv.invoice_link}
      target="_blank"
      rel="noopener noreferrer"
      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
    >
      הצג חשבונית
    </a>
  </div>
))}
    </div>
  )}
</div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-20 right-4 flex flex-col gap-2.5">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowRegularAppointmentModal(true)}
          className="p-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
        >
          <Repeat className="h-5 w-5" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('ui:open-new-appointment', {
                detail: { initialCustomerId: customer.id }
              })
            );
          }}
          className="p-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
        >
          <Calendar className="h-5 w-5" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => window.open(`tel:${customer.phone}`, '_blank')}
          className="p-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 active:bg-green-700 transition-colors"
        >
          <Phone className="h-5 w-5" />
        </motion.button>
        {customer.phone && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => window.open(`https://wa.me/${customer.phone.replace(/[^0-9]/g, '')}`, '_blank')}
            className="p-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 active:bg-green-700 transition-colors"
          >
            <WhatsAppIcon className="h-5 w-5" />
          </motion.button>
        )}
      </div>

      {/* Edit Form Modal */}
      <AnimatePresence>
        {showEditForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          >
            <CustomerForm
              customer={customer}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowEditForm(false);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regular Appointment Modal */}
      {showRegularAppointmentModal && (
        <RegularAppointmentModal
          onClose={() => setShowRegularAppointmentModal(false)}
          customer={customer}
        />
      )}

      {/* פופאפ מקצועי לחסימת לקוח */}
      <AnimatePresence>
        {showBlockPopup && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowBlockPopup(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full flex flex-col items-center relative"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowBlockPopup(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                aria-label="סגור"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="mb-4">
                <Ban className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-red-700 mb-2">הלקוח נחסם</h2>
              <p className="text-gray-700 text-center mb-4">
                לקוח זה נחסם בהצלחה. <br />
                לא יוכל לקבוע תורים חדשים או לבצע פעולות עד להסרת החסימה.<br />
                ניתן לשחרר את החסימה בכל עת דרך פרטי הלקוח.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CustomerDetails;